import { useCallback, useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import FollowTheSignsIcon from "@mui/icons-material/FollowTheSigns";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { GridColDef, GridRowModel } from "@mui/x-data-grid";

import {
  fetchDeleteStudents,
  fetchDeleteStudentsAll,
  fetchGetStudentsAll,
  fetchPostStudents,
  fetchPutStudent,
  fetchPutStudentsIncrementExceptFor,
  GRADES,
  PSDataGrid,
  schoolGrades,
  SetSnackType,
  SettingsGrades,
  STUDENT_ACHIEVEMENTS,
  STUDENT_BEHAVIOURS,
  STUDENT_ELLS,
  STUDENT_GENDERS,
  STUDENT_SOCIALS,
  studentAchievementsDescToIdMap,
  studentBehavioursDescToIdMap,
  StudentsNoNulls,
  studentSocialsDescToIdMap,
  TeachersNoNulls,
  Upload,
  UploadOverwriteDialog,
} from "../../utils";
import { AddStudentDropdownMenu } from ".";

const DoDelete = () => {
  return useCallback(
    (stu: StudentsNoNulls) =>
      new Promise<StudentsNoNulls>(async (resolve, reject) => {
        try {
          const result = (await fetchDeleteStudents([stu as StudentsNoNulls])) as StudentsNoNulls;
          resolve(result);
        } catch (err: any) {
          reject(err);
        }
      }),
    [],
  );
};

const DoPost = () => {
  return useCallback(
    (stu: StudentsNoNulls) =>
      new Promise<StudentsNoNulls>(async (resolve, reject) => {
        try {
          const result = (await fetchPostStudents([stu])) as StudentsNoNulls;
          resolve(result);
        } catch (err: any) {
          reject(err);
        }
      }),
    [],
  );
};

const DoPut = () => {
  return useCallback(
    (stu: StudentsNoNulls) =>
      new Promise<StudentsNoNulls>(async (resolve, reject) => {
        try {
          const updatedDoc = (await fetchPutStudent(stu)) as StudentsNoNulls;
          resolve(updatedDoc);
        } catch (err: any) {
          reject(err);
        }
      }),
    [],
  );
};

export const Students = (props: {
  disconnect: () => void;
  setSnack: SetSnackType;
  settingsGrades: SettingsGrades;
  teachers: TeachersNoNulls[];
}) => {
  const rowsOrder = (students: StudentsNoNulls[]): StudentsNoNulls[] =>
    students.toSorted(
      (a, b) =>
        GRADES.indexOf(a.grade) - GRADES.indexOf(b.grade) ||
        a.last.localeCompare(b.last) ||
        a.first.localeCompare(b.first),
    );

  const [rows, setRows] = useState(Array<StudentsNoNulls>());
  const [rowsLoading, setRowsLoading] = useState(false);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogCancelOpen, setAddDialogCancelOpen] = useState(false);
  // const [addStudent, setAddStudent] = useState({} as Partial<Student>); // todo
  const [addStudentNumber, setAddStudentNumber] = useState(null as number | null);
  const [addStudentLast, setAddStudentLast] = useState("");
  const [addStudentFirst, setAddStudentFirst] = useState("");
  const [addStudentGrade, setAddStudentGrade] = useState("");
  const [addStudentGender, setAddStudentGender] = useState("");
  const [addStudentIndigenous, setAddStudentIndigenous] = useState(false);
  const [addStudentAchievement, setAddStudentAchievement] = useState(0);
  const [addStudentBehaviour, setAddStudentBehaviour] = useState(0);
  const [addStudentSocial, setAddStudentSocial] = useState(0);
  const [addStudentELL, setAddStudentELL] = useState(0);
  const [addStudentSpEd1, setAddStudentSpEd1] = useState(0);
  const [addStudentSpEd2, setAddStudentSpEd2] = useState(0);
  const [addStudentSpEd3, setAddStudentSpEd3] = useState(0);
  const [addStudentSpClEd1, setAddStudentSpClEd1] = useState(0);
  const [addStudentSpClEd2, setAddStudentSpClEd2] = useState(0);

  const [addStudentNumberError, setAddStudentNumberError] = useState(false);
  const [addStudentLastError, setAddStudentLastError] = useState(false);
  const [addStudentFirstError, setAddStudentFirstError] = useState(false);
  const [addStudentGradeError, setAddStudentGradeError] = useState(false);
  const [addStudentSpEd1Error, setAddStudentSpEd1Error] = useState(false);
  const [addStudentSpEd2Error, setAddStudentSpEd2Error] = useState(false);
  const [addStudentSpEd3Error, setAddStudentSpEd3Error] = useState(false);
  const [addStudentSpClEd1Error, setAddStudentSpClEd1Error] = useState(false);
  const [addStudentSpClEd2Error, setAddStudentSpClEd2Error] = useState(false);

  const [incrementDialogOpen, setIncrementDialogOpen] = useState(false);
  const [incrementDialogWithholdOpen, setIncrementDialogWithholdOpen] = useState(false);
  const [withholdStudentNums, setWithholdStudentIds] = useState(Array<number>());

  const [actionDeleteStudentVisible, setActionDeleteStudentVisible] = useState(false);

  const [uploadPendingRows, setUploadPendingRows] = useState(null as Upload[] | null);
  const [openUploadOverwrite, setOpenUploadOverwrite] = useState(false);

  const addRow = DoPost();
  const removeRow = DoDelete();
  const mutateRow = DoPut();

  useEffect(() => {
    (async () => {
      setRowsLoading(true);
      const [resultStudents] = await Promise.all([fetchGetStudentsAll()]);
      if (Object.keys(resultStudents).includes("error")) {
        props.disconnect();
        props.setSnack((resultStudents as any).error?.toString(), "error");
      } else {
        setRows(rowsOrder(resultStudents as StudentsNoNulls[]));
      }
      setRowsLoading(false);
    })();
  }, []);

  const resetAddStudent = () => {
    setAddStudentNumber(null);
    setAddStudentFirst("");
    setAddStudentLast("");
    setAddStudentGrade("");
    setAddStudentGender("");
    setAddStudentIndigenous(false);
    setAddStudentAchievement(0);
    setAddStudentBehaviour(0);
    setAddStudentSocial(0);
    setAddStudentELL(0);
    setAddStudentSpEd1(0);
    setAddStudentSpEd2(0);
    setAddStudentSpEd3(0);
    setAddStudentSpClEd1(0);
    setAddStudentSpClEd2(0);
  };

  const resetAddStudentErrors = () => {
    setAddStudentNumberError(false);
    setAddStudentFirstError(false);
    setAddStudentLastError(false);
    setAddStudentGradeError(false);
    setAddStudentSpEd1Error(false);
    setAddStudentSpEd2Error(false);
    setAddStudentSpEd3Error(false);
    setAddStudentSpClEd1Error(false);
    setAddStudentSpClEd2Error(false);
  };

  const validateAddStudent = useCallback(
    (student: StudentsNoNulls) => {
      resetAddStudentErrors();
      if (!student.number) {
        console.error("student:", student);
        setAddStudentNumberError(true);
        props.setSnack("Invalid Student Number", "error");
        return false;
      }
      if (!student.first) {
        console.error("student:", student);
        setAddStudentFirstError(true);
        props.setSnack("Invalid First Name", "error");
        return false;
      }
      if (!student.last) {
        console.error("student:", student);
        props.setSnack("Invalid Last Name", "error");
        setAddStudentLastError(true);
        return false;
      }
      if (!student.grade) {
        setAddStudentGradeError(true);
        props.setSnack("Invalid Grade", "error");
        return false;
      }
      if (student.sp_ed_1 < 0) {
        setAddStudentSpEd1Error(true);
        props.setSnack("Invalid Sp Ed 1", "error");
        return false;
      }
      if (student.sp_ed_2 < 0) {
        setAddStudentSpEd2Error(true);
        props.setSnack("Invalid Sp Ed 2", "error");
        return false;
      }
      if (student.sp_ed_3 < 0) {
        setAddStudentSpEd3Error(true);
        props.setSnack("Invalid Sp Ed 3", "error");
        return false;
      }
      if (student.spcl_ed_1 < 0) {
        setAddStudentSpClEd1Error(true);
        props.setSnack("Invalid Sp Cl Ed 1", "error");
        return false;
      }
      if (student.spcl_ed_2 < 0) {
        setAddStudentSpClEd2Error(true);
        props.setSnack("Invalid Sp Cl Ed 2", "error");
        return false;
      }
      return true;
    },
    [props],
  );

  const processRowRemove = useCallback(
    async (stu: StudentsNoNulls) => {
      setRowsLoading(true);
      try {
        const res = (await removeRow(stu)) as any;
        if (Object.keys(res).includes("error")) throw res;
        setRows(r => rowsOrder(r.filter(i => i.id !== stu.id)));
        props.setSnack("Student successfully deleted", "success");
      } catch (err: any) {
        console.error(err);
        props.setSnack(err.error ?? err.toString(), "error");
        if (err.error === "401: Unauthorized") props.disconnect();
      } finally {
        setActionDeleteStudentVisible(false);
        setRowsLoading(false);
      }
    },
    [props, removeRow],
  );

  const handleProcessDataGridRowUpdateError = useCallback(
    (error: Error) => {
      props.setSnack(error.message, "error");
    },
    [props],
  );

  const processRowAdd = useCallback(
    async (stu: StudentsNoNulls) => {
      setRowsLoading(true);
      try {
        const res = (await addRow(stu)) as any;
        if (Object.keys(res).includes("error")) throw res;
        const studentAdded = (res as StudentsNoNulls[])[0];
        setAddDialogOpen(false);
        setRows(r => rowsOrder([...r, studentAdded]));
        props.setSnack("Student successfully added", "success");
        resetAddStudent();
      } catch (err: any) {
        console.error(err);
        props.setSnack(`${err.error}: ${err.existing.first} ${err.existing.last} (#${err.existing.number})`, "error");
        if (err.error === "401: Unauthorized") props.disconnect();
      } finally {
        setRowsLoading(false);
      }
    },
    [props, addRow],
  );

  const processRowsAdds = useCallback(
    async (stu: StudentsNoNulls[]) => {
      setRowsLoading(true);
      try {
        const res = (await fetchPostStudents(stu)) as any;
        if (Object.keys(res).includes("error") || res.acknowledged === false) throw res;
        props.setSnack(`Successfully added ${res.length} Students`, "success");
        setRows(r => [...r, ...(res as StudentsNoNulls[])]);
      } catch (err: any) {
        console.error(err);
        props.setSnack(
          err.error.includes('error: duplicate key value violates unique constraint "')
            ? "Failed: duplicates were present"
            : (err.error ?? err.toString()),
          "error",
        );
      } finally {
        setRowsLoading(false);
      }
    },
    [props],
  );

  const processRowUpdate = useCallback(
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      let diff = false;
      for (const k of Object.keys(newRow)) {
        if (newRow[k] !== oldRow[k]) {
          diff = true;
          break;
        }
      }
      if (!diff) return oldRow;

      const newStu = newRow as StudentsNoNulls;
      if (!validateAddStudent(newStu)) return oldRow;

      setRowsLoading(true);
      try {
        const res = await mutateRow(newStu);
        props.setSnack("Student successfully updated", "success");
        setRows(r => rowsOrder(r.map(iRow => (iRow.id === res.id ? res : iRow))));
        return res;
      } catch (err: any) {
        console.error(err);
        props.setSnack(`${err.error}`, "error");
        if (err.error === "401: Unauthorized") props.disconnect();
      } finally {
        setRowsLoading(false);
      }
    },
    [props, mutateRow, validateAddStudent],
  );

  const performUpload = async (overwrite: boolean) => {
    try {
      if (overwrite) {
        const dropped = await fetchDeleteStudentsAll();
        console.log(dropped);
        setRows([]);
      }
      await processRowsAdds(uploadPendingRows as StudentsNoNulls[]);
    } catch (err: any) {
      console.error(err);
    } finally {
      setUploadPendingRows(null);
      setOpenUploadOverwrite(false);
    }
  };

  const uploadStudents = (uploadedRows: Upload[]) => {
    setUploadPendingRows(uploadedRows);
  };

  useEffect(() => {
    if (!uploadPendingRows) return;
    if (rows.length > 0) {
      setOpenUploadOverwrite(true);
    } else {
      performUpload(false);
    }
  }, [uploadPendingRows]);

  const handleIncrement = useCallback(
    async (studentsToWithhold: number[]) => {
      setRowsLoading(true);
      try {
        const before = rows.length;
        const res = await fetchPutStudentsIncrementExceptFor(studentsToWithhold);
        if (Object.keys(res).includes("error")) throw (res as any).error;
        props.setSnack(
          `Successfully incremented ${res as number} students, and ${before - studentsToWithhold.length - (res as number)} archived`,
          "success",
        );
        setRows(rowsOrder((await fetchGetStudentsAll()) as StudentsNoNulls[]));
      } catch (err: any) {
        console.error(err);
        props.setSnack(`Unable to increment students: ${err}`, "error");
      } finally {
        setRowsLoading(false);
        setWithholdStudentIds([]);
      }
    },
    [props],
  );

  const columnsStudents: GridColDef[] = [
    {
      field: "actionDelete",
      headerAlign: "center",
      headerName: "Delete",
      width: 70,
      minWidth: 70,
      align: "center",
      sortable: false,
      renderCell: params => (
        <Button startIcon={<DeleteIcon />} onClick={ev => processRowRemove(params.row as StudentsNoNulls)} />
      ),
    },
    {
      field: "grade",
      type: "singleSelect",
      valueOptions: schoolGrades(props.settingsGrades),
      headerName: "Grade",
      width: 90,
      minWidth: 90,
      editable: true,
      valueGetter: (value: string) => value ?? "",
    },
    { field: "last", type: "string", headerName: "Last Name", flex: 1, maxWidth: 250, editable: true },
    { field: "first", type: "string", headerName: "First Name", flex: 1, maxWidth: 250, editable: true },
    {
      field: "number",
      type: "number",
      headerName: "ID #",
      headerAlign: "left",
      width: 96,
      minWidth: 96,
      align: "left",
      editable: true,
    },
    {
      field: "gender",
      type: "singleSelect",
      valueOptions: STUDENT_GENDERS,
      headerName: "Gender",
      width: 70,
      minWidth: 70,
      editable: true,
      valueGetter: (value: string) => value ?? "",
    },
    { field: "indigenous", type: "boolean", headerName: "Indigenous", width: 100, minWidth: 100, editable: true },
    {
      field: "achievement_id",
      type: "singleSelect",
      valueOptions: STUDENT_ACHIEVEMENTS.map(i => ({ value: i.id, label: i.desc })),
      headerName: "Achievement",
      width: 110,
      minWidth: 110,
      editable: true,
    },
    {
      field: "behaviour_id",
      type: "singleSelect",
      valueOptions: STUDENT_BEHAVIOURS.map(i => ({ value: i.id, label: i.desc })),
      headerName: "Behaviour",
      width: 105,
      minWidth: 105,
      editable: true,
    },
    {
      field: "social_id",
      type: "singleSelect",
      valueOptions: STUDENT_SOCIALS.map(i => ({ value: i.id, label: i.desc })),
      headerName: "Social",
      width: 130,
      minWidth: 130,
      editable: true,
    },
    {
      field: "ell",
      type: "singleSelect",
      valueOptions: STUDENT_ELLS,
      headerName: "ELL",
      width: 70,
      minWidth: 70,
      editable: true,
      valueGetter: (value: number) => (!value ? 0 : value),
    },
    {
      field: "sp_ed_1",
      type: "number",
      headerName: "1",
      width: 40,
      minWidth: 40,
      editable: true,
      valueGetter: (value: number) => (!value ? 0 : value),
    },
    {
      field: "sp_ed_2",
      type: "number",
      headerName: "2",
      width: 40,
      minWidth: 40,
      editable: true,
      valueGetter: (value: number) => (!value ? 0 : value),
    },
    {
      field: "sp_ed_3",
      type: "number",
      headerName: "3",
      width: 40,
      minWidth: 40,
      editable: true,
      valueGetter: (value: number) => (!value ? 0 : value),
    },
    {
      field: "spcl_ed_1",
      type: "number",
      headerName: "1",
      width: 40,
      minWidth: 40,
      editable: true,
      valueGetter: (value: number) => (!value ? 0 : value),
    },
    {
      field: "spcl_ed_2",
      type: "number",
      headerName: "2",
      width: 40,
      minWidth: 40,
      editable: true,
      valueGetter: (value: number) => (!value ? 0 : value),
    },
  ];

  return (
    <>
      <PSDataGrid
        actionDeleteVisible={actionDeleteStudentVisible}
        columns={columnsStudents}
        initialSortField="grade"
        loading={rowsLoading}
        onProcessRowUpdateError={handleProcessDataGridRowUpdateError}
        processRowUpdate={processRowUpdate}
        psType={"Students"}
        rows={rows}
        setActionDeleteVisible={setActionDeleteStudentVisible}
        setAddDialogOpen={setAddDialogOpen}
        setSnack={props.setSnack}
        teachers={props.teachers}
        toolbarElementsToAdd={[
          <Button onClick={() => setIncrementDialogOpen(true)} startIcon={<FollowTheSignsIcon />} variant="contained">
            Increment Grades
          </Button>,
        ]}
        toolbarHeaderRow={
          <Stack direction="row">
            <Box sx={{ flex: 1, maxWidth: 1466 - 120 - 80 }}></Box>
            <Box
              sx={{
                display: "flex",
                width: 120,
                maxWidth: 120,
                justifyContent: "center",
                fontWeight: "bold",
              }}
            >
              SpEd:
            </Box>
            <Box
              sx={{
                display: "flex",
                width: 80,
                maxWidth: 80,
                justifyContent: "center",
                fontWeight: "bold",
              }}
            >
              SpClEd:
            </Box>
          </Stack>
        }
        upload={uploadStudents}
      />
      <Dialog open={addDialogOpen}>
        <DialogTitle>Add Student</DialogTitle>
        <DialogContent>
          <DialogContentText>Asterisks indicate required fields.</DialogContentText>
          <TextField
            id="add-number"
            autoFocus
            error={addStudentNumberError}
            fullWidth
            label="Student ID Number"
            margin="dense"
            required
            type="number"
            variant="standard"
            onChange={ev => {
              setAddStudentNumberError(false);
              setAddStudentNumber(!ev.target.value ? null : Number(ev.target.value));
            }}
            onWheel={ev => (ev.target as HTMLInputElement).blur()}
          />
          <TextField
            id="add-last"
            error={addStudentLastError}
            fullWidth
            label="Last Name"
            margin="dense"
            required
            type="text"
            variant="standard"
            onChange={ev => {
              setAddStudentLastError(false);
              setAddStudentLast(ev.target.value ?? "");
            }}
          />
          <TextField
            id="add-first"
            error={addStudentFirstError}
            fullWidth
            label="First Name"
            margin="dense"
            required
            type="text"
            variant="standard"
            onChange={ev => {
              setAddStudentFirstError(false);
              setAddStudentFirst(ev.target.value ?? "");
            }}
          />
          <FormControl fullWidth required sx={{ my: 1 }}>
            <InputLabel id="add-stu-grade-label">Grade</InputLabel>
            <Select
              id="add-stu-grade"
              error={addStudentGradeError}
              label="Grade"
              labelId="add-stu-grade-label"
              onChange={ev => {
                setAddStudentGradeError(false);
                setAddStudentGrade(ev.target.value ?? "");
              }}
              value={addStudentGrade}
            >
              {schoolGrades(props.settingsGrades).map(gr => (
                <MenuItem key={gr} value={gr}>
                  {gr.replace("Gr", "Grade")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <AddStudentDropdownMenu
            label="Gender"
            options={STUDENT_GENDERS.map(i => ({ id: i, desc: i }))}
            stateVal={addStudentGender ?? ""}
            stateValSet={setAddStudentGender}
          />
          Indigenous
          <Checkbox onChange={ev => setAddStudentIndigenous(ev.target.checked)} />
          <AddStudentDropdownMenu
            label="Achievement"
            options={STUDENT_ACHIEVEMENTS}
            stateVal={addStudentAchievement}
            stateValSet={setAddStudentAchievement}
          />
          <AddStudentDropdownMenu
            label="Behaviour"
            options={STUDENT_BEHAVIOURS}
            stateVal={addStudentBehaviour}
            stateValSet={setAddStudentBehaviour}
          />
          <AddStudentDropdownMenu
            label="Social"
            options={STUDENT_SOCIALS}
            stateVal={addStudentSocial}
            stateValSet={setAddStudentSocial}
          />
          <AddStudentDropdownMenu
            label="ELL"
            options={STUDENT_ELLS.map(i => ({ id: i, desc: String(i) }))}
            stateVal={addStudentELL}
            stateValSet={setAddStudentELL}
          />
          <TextField
            id="add-sped1"
            autoFocus
            error={addStudentSpEd1Error}
            fullWidth
            label="Sp Ed 1"
            margin="dense"
            type="number"
            variant="standard"
            onChange={ev => setAddStudentSpEd1(!ev.target.value ? 0 : Number(ev.target.value))}
            onWheel={ev => (ev.target as HTMLInputElement).blur()}
          />
          <TextField
            id="add-sped2"
            autoFocus
            error={addStudentSpEd2Error}
            fullWidth
            label="Sp Ed 2"
            margin="dense"
            type="number"
            variant="standard"
            onChange={ev => setAddStudentSpEd2(!ev.target.value ? 0 : Number(ev.target.value))}
            onWheel={ev => (ev.target as HTMLInputElement).blur()}
          />
          <TextField
            id="add-sped3"
            autoFocus
            error={addStudentSpEd3Error}
            fullWidth
            label="Sp Ed 3"
            margin="dense"
            type="number"
            variant="standard"
            onChange={ev => setAddStudentSpEd3(!ev.target.value ? 0 : Number(ev.target.value))}
            onWheel={ev => (ev.target as HTMLInputElement).blur()}
          />
          <TextField
            id="add-spcled1"
            autoFocus
            error={addStudentSpClEd1Error}
            fullWidth
            label="Sp Cl Ed 1"
            margin="dense"
            type="number"
            variant="standard"
            onChange={ev => setAddStudentSpClEd1(!ev.target.value ? 0 : Number(ev.target.value))}
            onWheel={ev => (ev.target as HTMLInputElement).blur()}
          />
          <TextField
            id="add-spcled2"
            autoFocus
            error={addStudentSpClEd2Error}
            fullWidth
            label="Sp Cl Ed 2"
            margin="dense"
            type="number"
            variant="standard"
            onChange={ev => setAddStudentSpClEd2(!ev.target.value ? 0 : Number(ev.target.value))}
            onWheel={ev => (ev.target as HTMLInputElement).blur()}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (
                !schoolGrades(props.settingsGrades).includes(addStudentGrade ?? "") &&
                (addStudentLast ?? "") === "" &&
                (addStudentFirst ?? "") === "" &&
                (addStudentNumber ?? 0) === 0 &&
                (addStudentGender ?? "") === "" &&
                addStudentAchievement === studentAchievementsDescToIdMap.get("None") &&
                addStudentBehaviour === studentBehavioursDescToIdMap.get("None") &&
                addStudentSocial === studentSocialsDescToIdMap.get("None") &&
                addStudentELL === 0 &&
                addStudentSpEd1 === 0 &&
                addStudentSpEd2 === 0 &&
                addStudentSpEd3 === 0 &&
                addStudentSpClEd1 === 0 &&
                addStudentSpClEd2 === 0
              ) {
                setAddDialogOpen(false);
                resetAddStudentErrors();
              } else {
                setAddDialogCancelOpen(true);
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              const studentToAdd = {
                grade: addStudentGrade,
                last: addStudentLast,
                first: addStudentFirst,
                number: addStudentNumber ?? 0,
                gender: addStudentGender,
                indigenous: addStudentIndigenous,
                achievement_id: addStudentAchievement,
                behaviour_id: addStudentBehaviour,
                social_id: addStudentSocial,
                ell: addStudentELL,
                sp_ed_1: addStudentSpEd1,
                sp_ed_2: addStudentSpEd2,
                sp_ed_3: addStudentSpEd3,
                spcl_ed_1: addStudentSpClEd1,
                spcl_ed_2: addStudentSpClEd2,
              } as StudentsNoNulls;
              if (!validateAddStudent(studentToAdd)) return;
              processRowAdd(studentToAdd);
            }}
            variant="contained"
          >
            Add Student
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={addDialogCancelOpen}>
        <DialogContent>
          <DialogTitle>Cancel anyway?</DialogTitle>
          <DialogContentText>If you cancel now, you'll lose all current data entered.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogCancelOpen(false)}>Continue editing</Button>
          <Button
            onClick={() => {
              resetAddStudent();
              resetAddStudentErrors();
              setAddDialogCancelOpen(false);
              setAddDialogOpen(false);
            }}
            variant="outlined"
          >
            Discard & Close
          </Button>
        </DialogActions>
      </Dialog>
      <UploadOverwriteDialog
        open={openUploadOverwrite}
        upload={performUpload}
        uploadCancel={() => {
          setUploadPendingRows(null);
          setOpenUploadOverwrite(false);
        }}
      />
      <Dialog open={incrementDialogOpen}>
        <DialogTitle>Increment Grades</DialogTitle>
        <DialogContent>
          <DialogContentText>Do you want to increment all students?</DialogContentText>
          <DialogContentText>
            If you don't specify which students to withhold in their current grade, all students will be incremented by
            +1 grade.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            onClick={() => {
              setIncrementDialogOpen(false);
              setWithholdStudentIds([]);
            }}
          >
            No, cancel
          </Button>
          <Button
            onClick={() => {
              setIncrementDialogOpen(false);
              setIncrementDialogWithholdOpen(true);
            }}
            variant="outlined"
          >
            With holdbacks
          </Button>
          <Button
            onClick={() => {
              handleIncrement([]); // no holdbacks
              setIncrementDialogOpen(false);
            }}
            variant="contained"
          >
            Yes, increment
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={incrementDialogWithholdOpen}>
        <DialogContent>
          <DialogTitle>Withhold one or more Students</DialogTitle>
          <DialogContentText>
            Select which students should be excluded from being incremented, withholding them to remain in their
            respective grade.
          </DialogContentText>
          <List>
            {rows.map(s => (
              <ListItem disablePadding key={s.id}>
                <ListItemButton
                  dense
                  onClick={ev => {
                    const currentIndex = withholdStudentNums.indexOf(s.id);
                    const newIds = [...withholdStudentNums];
                    if (currentIndex === -1) {
                      newIds.push(s.id);
                    } else {
                      newIds.splice(currentIndex, 1);
                    }
                    setWithholdStudentIds(newIds);
                  }}
                  role={undefined}
                >
                  <ListItemIcon>
                    <Checkbox checked={withholdStudentNums.indexOf(s.id) !== -1} edge="start" />
                  </ListItemIcon>
                  <ListItemText
                    id={String(s.id)}
                    secondary={
                      <>
                        {`${s.grade.replace("Gr", "Grade")}`}
                        <Typography color="text.primary" component="span" sx={{ display: "inline", mx: 1 }}>
                          {`${s.first} ${s.last}`}
                        </Typography>
                        {`${s.number}`}
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIncrementDialogWithholdOpen(false);
              setIncrementDialogOpen(true);
            }}
            variant="outlined"
          >
            Go back
          </Button>
          <Button
            disabled={rows.length === withholdStudentNums.length}
            onClick={() => {
              handleIncrement(withholdStudentNums);
              setIncrementDialogWithholdOpen(false);
              setIncrementDialogOpen(false);
            }}
            variant="contained"
          >
            {`Increment ${withholdStudentNums.length === 0 ? "all " : ""} ${rows.length - withholdStudentNums.length} ${
              withholdStudentNums.length === 0 ? "" : `of ${rows.length} `
            }students`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
